import { useState, useRef, useEffect, useCallback } from 'react';

export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

export const US_STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'District of Columbia',
};

export const CA_PROVINCES = [
  'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT',
];

export const CA_PROVINCE_NAMES: Record<string, string> = {
  AB:'Alberta',BC:'British Columbia',MB:'Manitoba',NB:'New Brunswick',
  NL:'Newfoundland and Labrador',NS:'Nova Scotia',NT:'Northwest Territories',
  NU:'Nunavut',ON:'Ontario',PE:'Prince Edward Island',QC:'Quebec',
  SK:'Saskatchewan',YT:'Yukon',
};

interface StateAutocompleteProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  country?: string;
}

export function StateAutocomplete({
  value,
  onChange,
  placeholder,
  className,
  country = 'US',
}: StateAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const states = country === 'CA' ? CA_PROVINCES : US_STATES;
  const names = country === 'CA' ? CA_PROVINCE_NAMES : US_STATE_NAMES;
  const defaultPlaceholder = country === 'CA' ? 'Province / Territory' : 'State / Province';

  const query = (value ?? '').trim().toLowerCase();
  const filtered = query
    ? states.filter(
        (s) => s.toLowerCase().startsWith(query) || names[s]?.toLowerCase().startsWith(query),
      )
    : states;

  const showDropdown = open && filtered.length > 0;

  const selectState = useCallback(
    (s: string) => {
      onChange(s);
      setOpen(false);
      setHighlightIdx(-1);
    },
    [onChange],
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!showDropdown) {
      if (e.key === 'ArrowDown') {
        setOpen(true);
        setHighlightIdx(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => (i < filtered.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => (i > 0 ? i - 1 : filtered.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        selectState(filtered[highlightIdx]);
      }
    } else if (e.key === 'Escape') {
      // Contain Escape to the dropdown (this handler already returned early
      // when closed) so it can't reach an enclosing Modal and close it.
      e.stopPropagation();
      setOpen(false);
      setHighlightIdx(-1);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlightIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? defaultPlaceholder}
        className={className ?? 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary'}
        autoComplete="off"
      />
      {showDropdown && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {filtered.map((s, i) => (
            <li
              key={s}
              onMouseDown={(e) => {
                e.preventDefault();
                selectState(s);
              }}
              className={`cursor-pointer px-3 py-2 text-sm ${
                i === highlightIdx ? 'bg-primary/10 text-primary' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="font-medium">{s}</span>
              <span className="ml-1.5 text-slate-400">{names[s]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
